import type { LimitsStatusDataSignature } from 'tio-common/services/tuition-assistance';

/**
 * What the guided wizard's benefit-limit submit gate should do, as data. The
 * wizard turns each state into a `submitBlockers` sentence (or none), so the
 * decision logic lives here where it can be unit-tested without mounting the
 * 2000-line wizard.
 *
 * Semantics mirror `<LimitsSummary>`'s employee-side gate
 * (tio-common/components/tuition-assistance/limits-summary.gts), with one
 * deliberate tightening: `verifying` and `unverified` both block, closing the
 * gate-open-while-loading window the shared component has. The post-click
 * `validateAmountRequestedAgainstCurrentLimitsStatus` backstop in the wizard's
 * submitTask is unchanged and still catches a stale fetch.
 */
export type BenefitLimitGate =
  /** No basis date set, or the program allows submission beyond limits — no gate. */
  | { state: 'ungated' }
  /** Fetch in flight — fail closed until we know. */
  | { state: 'verifying' }
  /** Fetch completed without a usable status (failed request, missing employee) — fail closed. */
  | { state: 'unverified' }
  /** Unlimited remaining benefit, or the request fits. */
  | { state: 'within-limit' }
  | { state: 'over-limit'; reductionNeeded: number; isCredits: boolean };

export function computeBenefitLimitGate(params: {
  /** The limit-basis course date is set, so a status fetch is meaningful (wizard's `shouldFetchLimits`). */
  shouldFetchLimits: boolean;
  /** `application.ignorePolicies`, or ALLOW_PRE_APPROVAL_SUBMISSION_BEYOND_LIMIT on the template. */
  allowedBeyondLimits: boolean;
  isLoading: boolean;
  /** Already failure-resolved: pass undefined when the most recent fetch failed. */
  limitStatus: LimitsStatusDataSignature | undefined;
  /** Credit-based total on the application (used when the limit is in credits). */
  totalCredits: number;
  /** Cents the submit will stamp: the request net of scholarships, capped at the GPA amount on GPA programs. */
  requestedAmount: number;
  /**
   * Cents of expenses the employee entered, net of scholarships. A reduction has to come
   * out of these, so it is measured from the larger of this and `requestedAmount`.
   */
  requestedTotal: number;
}): BenefitLimitGate {
  const { shouldFetchLimits, allowedBeyondLimits, isLoading, limitStatus } = params;

  if (!shouldFetchLimits || allowedBeyondLimits) {
    return { state: 'ungated' };
  }
  if (isLoading) {
    return { state: 'verifying' };
  }
  if (!limitStatus) {
    return { state: 'unverified' };
  }

  const remainingBenefit = limitStatus.available ?? null;
  if (remainingBenefit === null) {
    // `available: null` on a real status means unlimited — not unknown.
    return { state: 'within-limit' };
  }

  const isCredits = limitStatus.limit_capacity === 'TAS.LimitCapacity.CREDITS';
  const amountRequested = isCredits ? params.totalCredits : params.requestedAmount;
  if (remainingBenefit - amountRequested >= 0) {
    return { state: 'within-limit' };
  }
  const reductionBasis = isCredits
    ? amountRequested
    : Math.max(amountRequested, params.requestedTotal);
  return { state: 'over-limit', reductionNeeded: reductionBasis - remainingBenefit, isCredits };
}
